import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, AreaChart,
} from "recharts";
import {
  Users, Target, MessageSquare, Bot, Globe, Zap, TrendingUp, Sparkles, CheckCircle2,
  ArrowUpRight, Star, Calendar, FileText, DollarSign,
} from "lucide-react";

function usePlugitaiVanity(activeSlug) {
  // Deterministic vanity metrics for the PlugitAI agency dashboard.
  // Only applied when the active tenant is PlugitAI — real numbers still show for
  // Lone Star, BestRx, LeenRx, etc.
  const isPlugitai = activeSlug === "plugitai";

  return useState(() => {
    if (!isPlugitai) return null;

    // Anchor date: start of 2026 (arbitrary but stable across all viewers)
    const anchor = new Date("2026-01-02T18:00:00-06:00"); // First Friday 6pm CT of 2026
    const now = new Date();
    const msPerWeek = 7 * 24 * 60 * 60 * 1000;
    let weeks = Math.max(0, Math.floor((now - anchor) / msPerWeek));
    // Bump only after Friday 6pm CT
    const dow = now.getDay(); // 0=Sun..5=Fri..6=Sat
    const passedFri6pm =
      dow > 5 ||
      (dow === 5 && (now.getHours() > 18 || (now.getHours() === 18 && now.getMinutes() >= 0)));
    if (!passedFri6pm && dow < 5) weeks = Math.max(0, weeks - 1);

    // Revenue climbs $2,340 every Friday 6pm, starting at $18,500 base
    const revenue = 18500 + weeks * 2340;

    // SEO score oscillates 92-100 deterministically by day-of-year
    const start = new Date(now.getFullYear(), 0, 0);
    const dayOfYear = Math.floor((now - start) / (1000 * 60 * 60 * 24));
    const seoScore = 92 + (dayOfYear % 9);

    return { revenue, seoScore, websitesLive: 22, weeks };
  })[0];
}

function KpiCard({ label, value, delta, icon: Icon, color = "blue", href }) {
  const Wrapper = href ? Link : "div";
  return (
    <Wrapper
      to={href}
      className={`n-card n-card-hover p-5 ${href ? "cursor-pointer" : ""} count-up`}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="n-label">{label}</div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="n-stat text-4xl">{value}</div>
            {delta && <span className={`text-xs font-semibold ${delta.startsWith("+") ? "text-[var(--n-green)]" : "text-[var(--n-danger)]"}`}>{delta}</span>}
          </div>
        </div>
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-[var(--n-${color}-soft)]`}>
          <Icon size={18} className={`text-[var(--n-${color})]`}/>
        </div>
      </div>
    </Wrapper>
  );
}

export default function Dashboard() {
  const { activeTenant } = useTenant();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [convos, setConvos] = useState([]);
  const [leads, setLeads] = useState([]);
  const vanity = usePlugitaiVanity(activeTenant?.slug);

  useEffect(() => {
    if (!activeTenant) return;
    api.get("/analytics/summary").then(({ data }) => setData(data)).catch(() => {});
    api.get("/conversations").then(({ data }) => setConvos(data.slice(0, 4))).catch(() => {});
    api.get("/leads").then(({ data }) => setLeads(data.slice(0, 5))).catch(() => {});
    // eslint-disable-next-line
  }, [activeTenant?.id]);

  if (!activeTenant) return <div className="p-10 text-[var(--n-text-muted)]">Select a tenant.</div>;

  const firstName = (user?.name || "").split(" ")[0] || "there";
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="p-8 max-w-[1500px] mx-auto" data-testid="dashboard-page">
      {/* Welcome */}
      <div className="mb-8 fade-up">
        <div className="text-4xl md:text-5xl font-cab font-black tracking-tight">
          {greet}, <span className="gradient-tri">{firstName}</span>.
        </div>
        <div className="text-[var(--n-text-sec)] mt-2 text-lg">
          Here&apos;s what&apos;s happening across {activeTenant.name}.
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4 mb-8">
        <KpiCard label="Leads" value={data?.total_leads ?? "—"} delta="+12%" icon={Users} color="blue" href="/admin/leads" />
        <KpiCard label="Conversion" value={data ? `${data.conversion_rate}%` : "—"} icon={Target} color="green" />
        <KpiCard label="Conversations" value={convos.length || 0} delta="+8%" icon={MessageSquare} color="blue" href="/admin/messages" />
        <KpiCard label="AI Resolved" value={convos.filter(c => (c.unread_admin_count || 0) === 0).length} icon={Bot} color="orange" />
        <KpiCard label="Websites"
                 value={vanity ? `${vanity.websitesLive} live` : "1 live"}
                 delta={vanity ? "+3 this Summer" : undefined}
                 icon={Globe} color="green" href="/admin/website/pages" />
        <KpiCard label="Revenue"
                 value={vanity ? `$${vanity.revenue.toLocaleString()}` : "$0"}
                 delta={vanity ? "+$2,340 / wk" : "Stripe soon"}
                 icon={DollarSign} color="green" />
        <KpiCard label="SEO Score"
                 value={vanity ? String(vanity.seoScore) : "92"}
                 delta={vanity ? "+2" : undefined}
                 icon={Sparkles} color="orange" href="/admin/seo" />
        <KpiCard label="Automations" value="0" icon={Zap} color="orange" href="/admin/automation" />
        <KpiCard label="Messages" value={data?.total_messages ?? "—"} icon={MessageSquare} color="blue" />
        <KpiCard label="Appointments" value="—" icon={Calendar} color="blue" />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-8">
        <div className="n-card p-5 lg:col-span-2 fade-up delay-1" data-testid="leads-trend-chart">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="n-label">Lead growth · 14 days</div>
              <div className="font-cab font-black text-2xl mt-1">Acquisition</div>
            </div>
            <div className="flex items-center gap-1 text-xs text-[var(--n-green)] font-semibold">
              <TrendingUp size={12}/> trending up
            </div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={data?.timeseries || []}>
                <defs>
                  <linearGradient id="fillBlue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.4}/>
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E7EB" vertical={false}/>
                <XAxis dataKey="date" stroke="#94A3B8" tick={{ fontSize: 10 }} tickFormatter={(d) => d?.slice(5)}/>
                <YAxis stroke="#94A3B8" tick={{ fontSize: 10 }} allowDecimals={false}/>
                <Tooltip contentStyle={{ background: "white", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }}/>
                <Area type="monotone" dataKey="leads" stroke="#2563EB" strokeWidth={2.5} fill="url(#fillBlue)"/>
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="n-card p-5 fade-up delay-2" data-testid="ai-performance">
          <div className="n-label">AI Performance</div>
          <div className="font-cab font-black text-2xl mt-1 mb-5">Employees on duty</div>
          {[
            { name: "Customer Support AI", stat: "98% CSAT", pct: 98, color: "green" },
            { name: "Sales AI", stat: "47 replies", pct: 76, color: "blue" },
            { name: "Review AI", stat: "12 pending", pct: 45, color: "orange" },
          ].map((a) => (
            <div key={a.name} className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Bot size={12} className={`text-[var(--n-${a.color})]`}/> {a.name}
                </div>
                <span className={`text-xs font-semibold text-[var(--n-${a.color})]`}>{a.stat}</span>
              </div>
              <div className="h-1.5 bg-[var(--n-surface)] rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${a.pct}%`, background: `var(--n-${a.color})` }}/>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CRM + Activity feed */}
      <div className="grid lg:grid-cols-2 gap-4 mb-8">
        <div className="n-card p-5 fade-up delay-3" data-testid="recent-leads">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="n-label">CRM</div>
              <div className="font-cab font-black text-2xl mt-1">Recent leads</div>
            </div>
            <Link to="/admin/leads" className="text-sm text-[var(--n-blue)] font-semibold hover:underline flex items-center gap-1">
              View all <ArrowUpRight size={12}/>
            </Link>
          </div>
          <div className="space-y-2">
            {leads.map((l) => (
              <div key={l.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[var(--n-surface)] transition-colors" data-testid={`recent-lead-${l.id}`}>
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold bg-gradient-to-br from-[var(--n-blue)] to-[var(--n-green)]">
                  {l.name[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{l.name}</div>
                  <div className="text-xs text-[var(--n-text-muted)] truncate">{l.email || l.phone || "—"}</div>
                </div>
                <span className={`n-chip n-chip-${l.status === "won" ? "green" : l.status === "qualified" ? "blue" : l.status === "lost" ? "" : "orange"}`}>
                  {l.status}
                </span>
              </div>
            ))}
            {leads.length === 0 && <div className="text-sm text-[var(--n-text-muted)] p-4 text-center">No leads yet.</div>}
          </div>
        </div>

        <div className="n-card p-5 fade-up delay-4" data-testid="activity-feed">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="n-label">Live</div>
              <div className="font-cab font-black text-2xl mt-1">Activity</div>
            </div>
            <div className="flex items-center gap-1 text-xs text-[var(--n-green)]">
              <span className="w-2 h-2 rounded-full bg-[var(--n-green)] pulse-dot"/> real-time
            </div>
          </div>
          <div className="space-y-3">
            {[
              { icon: Users, color: "blue", text: "Lead captured", sub: leads[0]?.name || "New patient inquiry", time: "just now" },
              { icon: Bot, color: "orange", text: "AI conversation resolved", sub: "prescription transfer question", time: "2m ago" },
              { icon: MessageSquare, color: "blue", text: "Message sent", sub: "Follow-up SMS", time: "5m ago" },
              { icon: FileText, color: "green", text: "Website published", sub: "Home page updated", time: "1h ago" },
              { icon: Star, color: "orange", text: "Review received", sub: "5-star from patient", time: "2h ago" },
              { icon: CheckCircle2, color: "green", text: "Automation completed", sub: "Welcome sequence · 12 recipients", time: "3h ago" },
            ].map((a, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-[var(--n-${a.color}-soft)] shrink-0`}>
                  <a.icon size={14} className={`text-[var(--n-${a.color})]`}/>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold">{a.text}</div>
                  <div className="text-xs text-[var(--n-text-muted)] truncate">{a.sub}</div>
                </div>
                <div className="text-[10px] text-[var(--n-text-muted)] whitespace-nowrap">{a.time}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tasks / today */}
      <div className="n-card p-5 fade-up delay-5" data-testid="tasks-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="n-label">Today</div>
            <div className="font-cab font-black text-2xl mt-1">Tasks &amp; approvals</div>
          </div>
        </div>
        <div className="grid md:grid-cols-4 gap-3">
          {[
            { label: "Website changes", count: 2, color: "blue" },
            { label: "SEO issues", count: 3, color: "orange" },
            { label: "Reviews pending", count: 1, color: "green" },
            { label: "Lead follow-ups", count: leads.filter((l) => l.status === "new").length, color: "blue" },
          ].map((t) => (
            <div key={t.label} className={`rounded-xl p-4 bg-[var(--n-${t.color}-soft)] cursor-pointer hover:opacity-90 transition-opacity`}>
              <div className={`text-xs font-semibold text-[var(--n-${t.color})] uppercase tracking-wide`}>{t.label}</div>
              <div className={`n-stat text-3xl mt-2 text-[var(--n-${t.color})]`}>{t.count}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
